import React, { FC, useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import StringUtils from '../../utils/stringUtils';
import { ReactComponent as PickupIcon } from '../../assets/icons/pickup_icon.svg';
import { ReactComponent as DeliveryIcon } from '../../assets/icons/delivery_icon.svg';
import { ReactComponent as StopIcon } from '../../assets/icons/stop_icon.svg';
import GroupIcon from '@mui/icons-material/Group';
import NumberUtils from '../../utils/numberUtils';
import TwLoadTags from '../TwLoadTag/TwLoadTag';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { TwLoadTooltipInfoProps } from './TwLoadTooltipInfo';
import { ORDER_STATUS, OrderStatusColor } from '../../redux/api/orders/orders.model';
import { useGetOrderByIdQuery } from '../../redux/api/orders/ordersApi';
import CircularProgress from '@mui/material/CircularProgress';
import useAuth from '../../providers/Auth/Auth.provider';
import DateUtils from '../../utils/dateUtils';
import { EntityType } from '../../redux/Global.model';

export const PickupDeliveryWrapper = styled('div')(() => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  marginLeft: '8px'
}));

export const DateTimeWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignContent: 'right',
  minWidth: 'fit-content',
  justifyContent: 'space-between'
}));

export const DateTime = styled(Box)(() => ({
  color: '#9CA3AF',
  fontSize: '12px',
  lineHeight: '16px',
  textAlign: 'right',
  margin: '5px 0px 5px 0',
  display: 'flex',
  flexDirection: 'column'
}));

export const LocationIconsWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '6px 0 0 0'
}));

const TwLoadTooltipContent: FC<{ data?: TwLoadTooltipInfoProps; orderId?: string }> = ({
  data,
  orderId
}) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { enableFlexibleTagging } = useFeatureFlags();
  const use24HourClock = getTenantSetting('use-24-hour-clock', false);
  const [locationsLineHeight, setLocationsLineHeight] = useState(0);
  const [order, setOrder] = useState<TwLoadTooltipInfoProps | undefined>(data);
  const firstStopRef = useRef<HTMLDivElement | null>(null);
  const numberStopsRef = useRef<HTMLDivElement | null>(null);
  const { data: orderData, isLoading } = useGetOrderByIdQuery(
    { orderId: orderId || '' },
    {
      skip: !orderId
    }
  );

  const formatTime = (time?: string | null, date?: string): string => {
    if (!time) return '';
    if (!use24HourClock && !date) return '';

    if (!use24HourClock) {
      return DateUtils.get12HourFormattedTime(new Date(`${date}T${time.split('+')[0]}`));
    } else {
      return time?.slice(0, -3);
    }
  };

  // Effect in case order data does not come from parent component
  // in that case we fetch the data from the API
  // used in Trip table where trip search does not include order data
  useEffect(() => {
    if (orderData && orderId) {
      const segmentsCount = orderData?.trips.length || 0;

      let pickup, delivery, intermediateStops;

      if (!enableLtl) {
        pickup = orderData?.trips.length > 0 ? orderData?.trips[0].stops[0] : null;
        const lastSegment =
          orderData?.trips.length > 0 ? orderData?.trips[segmentsCount - 1] : null;
        delivery = lastSegment ? lastSegment?.stops[lastSegment.stops.length - 1] : null;
        intermediateStops =
          (orderData?.full_route_stop_overview &&
            orderData?.full_route_stop_overview.length > 0 &&
            orderData.full_route_stop_overview.length - 2) ||
          0;
      } else {
        (pickup = orderData.stops && orderData.stops.length > 0 ? orderData.stops[0] : null),
          (delivery =
            orderData.stops && orderData.stops.length > 0
              ? orderData.stops[orderData.stops.length - 1]
              : null);
        intermediateStops = orderData.stops.length - 2;
      }

      const tooltipData: TwLoadTooltipInfoProps = {
        tags: orderData.tags || [],
        loadStatus: orderData?.status || ORDER_STATUS.NOT_STARTED,
        loadId: orderData?.id || '',
        clientName: orderData.raw_customer?.name || '',
        rate: orderData?.rate || 0,
        ratePerMile: orderData?.rate_per_mile || 0,
        customerOrderNumber: orderData?.customer_order_number || '',
        pickupLocation: pickup?.location || null,
        deliveryLocation: delivery?.location || null,
        stops: intermediateStops,

        pickupDate:
          pickup && pickup.start_date
            ? DateUtils.getMediumDate(new Date(pickup.start_date)).split(',')[0]
            : '',
        pickupStartTime: formatTime(pickup?.start_time, pickup?.start_date),
        pickupEndTime: formatTime(pickup?.end_time, pickup?.start_date),
        deliveryDate:
          delivery && delivery.end_date
            ? DateUtils.getMediumDate(new Date(delivery?.end_date)).split(',')[0]
            : '',
        deliveryStartTime: formatTime(delivery?.start_time, delivery?.end_date),
        deliveryEndTime: formatTime(delivery?.end_time, delivery?.end_date),
        loadReadableId: orderData?.readable_id || 0,
        brokered: !!orderData?.is_brokered_order,
        pickupNumber: pickup?.stop_identifier || '',
        deliveryNumber: delivery?.stop_identifier || '',
        booking_type: orderData?.booking_type || ''
      };

      setOrder(tooltipData);
    }
  }, [orderData, orderId]);

  useEffect(() => {
    if (order) {
      let height = 0;
      if (firstStopRef.current) {
        height = firstStopRef.current.clientHeight;
      }

      if (numberStopsRef.current) {
        height += numberStopsRef.current.clientHeight + 8; // 8px for margin
      }

      setLocationsLineHeight(height);
    }
  }, [order]);

  const getLoadColor = (status: ORDER_STATUS): string => {
    const color = OrderStatusColor[status];

    if (!color || color === undefined) {
      return '#6B7280'; // Gray
    } else {
      return color;
    }
  };

  return (
    <Box>
      {isLoading && !order && (
        <Stack
          flexDirection={'row'}
          justifyContent={'center'}
          sx={{ width: '50px', padding: '10px' }}
        >
          <CircularProgress size={20} />
        </Stack>
      )}

      {order && (
        <>
          <Stack
            className={`tw-load-tooltip__status`}
            sx={{
              backgroundColor: `${getLoadColor(order.loadStatus)}20`
            }}
          >
            <Typography
              className="tw-load-tooltip__status-title"
              sx={{ color: getLoadColor(order.loadStatus) }}
            >
              {StringUtils.getOrderStatusLabel(order.loadStatus)}
            </Typography>
          </Stack>

          <Box className="tw-load-tooltip__data-container">
            {enableFlexibleTagging && order.tags.length > 0 && (
              <TwLoadTags
                loadId={order.loadId}
                tags={order.tags}
                readOnly
                entity={EntityType.ORDER}
              />
            )}
            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              sx={{
                marginTop: enableFlexibleTagging && order.tags.length > 0 ? '16px' : '0'
              }}
            >
              <Stack className="tw-load-tooltip__client-name">
                <Typography>{order.clientName}</Typography>
              </Stack>
              <Stack className="tw-load-tooltip__rate-info">
                <Typography>
                  {order.rate === null || order.rate === 0
                    ? ''
                    : NumberUtils.getCurrency(order.rate, 0)}
                </Typography>
                <Typography component={'span'}>
                  {' '}
                  {!!order.ratePerMile && `${NumberUtils.getCurrency(order.ratePerMile)}/mi`}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              sx={{ marginTop: '16px' }}
            >
              <LocationIconsWrapper>
                <PickupIcon style={{ minWidth: '12px' }} />
                <Box
                  className="tw-load-tooltip__locations-line"
                  sx={{ height: locationsLineHeight }}
                ></Box>
                <DeliveryIcon style={{ minWidth: '12px' }} />
              </LocationIconsWrapper>

              <PickupDeliveryWrapper>
                <Stack
                  ref={(el): any => {
                    firstStopRef.current = el;
                  }}
                  className="tw-load-tooltip__locations-info"
                >
                  <p>{order.pickupLocation?.name}</p>
                  <p>{`${order.pickupLocation?.address_1}, ${order.pickupLocation?.city}, ${order.pickupLocation?.state}`}</p>
                  <p>{order.pickupNumber && `Pickup #${order.pickupNumber}`}</p>
                </Stack>

                {!!order.stops && (
                  <Box ref={numberStopsRef} className="tw-load-tooltip__locations-stops">
                    <StopIcon style={{ minWidth: '12px' }} />
                    {Number(order.stops) > 1 ? `+${order.stops} stops` : `+${order.stops} stop`}
                  </Box>
                )}

                <Stack className="tw-load-tooltip__locations-info">
                  <p>{order.deliveryLocation?.name}</p>
                  <p>{`${order.deliveryLocation?.address_1}, ${order.deliveryLocation?.city}, ${order.deliveryLocation?.state}`}</p>
                  <p>{order.deliveryNumber && `Delivery #${order.deliveryNumber}`}</p>
                </Stack>
              </PickupDeliveryWrapper>

              <DateTimeWrapper>
                <DateTime>
                  <span>{order.pickupDate}</span>
                  <span>{order.pickupStartTime}</span>
                  <span>{order.pickupEndTime}</span>
                </DateTime>
                <DateTime>
                  <span>{order.deliveryDate}</span>
                  <span>{order.deliveryStartTime}</span>
                  <span>{order.deliveryEndTime}</span>
                </DateTime>
              </DateTimeWrapper>
            </Stack>

            <Stack className="tw-load-tooltip__boxes-container">
              {order.customerOrderNumber && (
                <Box className="tw-load-tooltip__boxes">
                  <Typography>Customer order</Typography>
                  <Typography component={'span'}>{`#${order.customerOrderNumber}`}</Typography>
                </Box>
              )}
              <Box className="tw-load-tooltip__boxes">
                <Typography>Truckbase order</Typography>
                <Typography component={'span'}>{`#${order.loadReadableId}`}</Typography>
              </Box>
              <Box className="tw-load-tooltip__boxes">
                <Typography>Booking mode</Typography>
                <Typography component={'span'}>{order.booking_type}</Typography>
              </Box>
              {order.brokered && (
                <Box className="tw-load-tooltip__boxes">
                  <Typography>
                    <GroupIcon />
                  </Typography>
                  <Typography component={'span'}>Brokered</Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TwLoadTooltipContent;
